import { useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { InventoryItem, InventoryLocation, Order, PosSession, Product } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney, parseMoney } from '../finance/money';
import { useMe } from '../session/SessionProvider';
import { FULFILLMENT, NEXT_STEP, PAYMENT } from './labels';
import { PosPanel } from './PosPanel';
import { KitchenLinks } from './Kitchen';
import { CashierTab } from '../wallet/WalletPage';

type Tab = 'caja' | 'cocina' | 'monedero' | 'productos';
const TABS: Record<Tab, string> = { caja: 'Caja', cocina: 'Pedidos', monedero: 'Recargar monedero', productos: 'Productos' };
type Catalog = { products: Product[]; registers: { id: string; name: string }[] };
type CashData = Catalog & { sessions: PosSession[] };

const money = (c: number) => formatMoney(c, 'MXN');

/** Comercio (Motor 17): cafetería o librería de la iglesia. Caja por turnos, pedidos y catálogo. */
export function CommercePage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const canManage = me.can('commerce.manage');
  const [params, setParams] = useSearchParams();
  const tabs = (['caja', 'cocina', 'monedero', 'productos'] as Tab[]).filter((t) => (t === 'productos' ? canManage : t === 'monedero' ? me.can('commerce.sell') : true));
  const tab = tabs.find((t) => t === params.get('vista')) ?? 'caja';
  const [notice, setNotice] = useState<string | null>(null);
  const [walletMsg, setWalletMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page">
      <header className="page__header">
        <h1>Cafetería y tienda</h1>
        <p className="muted">Cafetería, librería o tienda de la iglesia.</p>
      </header>
      <div className="tabs" role="tablist" aria-label="Cafetería y tienda">
        {tabs.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className={`tabs__tab${tab === t ? ' tabs__tab--active' : ''}`}
            onClick={() => {
              setNotice(null);
              setWalletMsg(null);
              setParams({ vista: t });
            }}
          >
            {TABS[t]}
          </button>
        ))}
      </div>
      {notice ? <Alert tone="success">{notice}</Alert> : null}
      {tab === 'caja' ? <CashTab churchId={churchId} canManage={canManage} onNotice={setNotice} /> : null}
      {tab === 'cocina' ? <OrdersTab churchId={churchId} canManage={canManage} onNotice={setNotice} /> : null}
      {tab === 'monedero' ? (
        <>
          {walletMsg ? <Alert tone={walletMsg.tone}>{walletMsg.text}</Alert> : null}
          <CashierTab base={`/commerce/churches/${churchId}`} onMessage={setWalletMsg} />
        </>
      ) : null}
      {tab === 'productos' ? <ProductsTab churchId={churchId} onNotice={setNotice} /> : null}
    </div>
  );
}

// ── Caja ───────────────────────────────────────────────────────────────────

function CashTab({ churchId, canManage, onNotice }: { churchId: string; canManage: boolean; onNotice: (m: string | null) => void }) {
  const [version, setVersion] = useState(0);
  const [chosen, setChosen] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  const data = useAsync(
    async (signal): Promise<CashData> => {
      const [catalog, sessions] = await Promise.all([
        api.get<Catalog>(`/commerce/churches/${churchId}/catalog`, signal),
        api.get<{ items: PosSession[] }>(`/commerce/churches/${churchId}/sessions?open=true`, signal),
      ]);
      return { ...catalog, sessions: sessions.items };
    },
    [churchId, version],
  );
  const last = useRef<CashData | null>(null);
  if (data.status === 'ready') last.current = data.data;
  const d = data.status === 'ready' ? data.data : last.current;
  const refresh = (message: string) => {
    onNotice(message);
    setOpening(false);
    setVersion((v) => v + 1);
  };

  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  if (!d) return <Loading />;

  const session = d.sessions.find((s) => s.id === chosen) ?? d.sessions[0] ?? null;
  const freeRegisters = d.registers.filter((r) => !d.sessions.some((s) => s.register.id === r.id));

  if (d.registers.length === 0) {
    return canManage ? (
      <Card title="Crea tu primera caja">
        <p className="muted small">Una caja es un punto de venta: la cafetería, la mesa de libros…</p>
        <NewRegisterForm churchId={churchId} onDone={refresh} />
      </Card>
    ) : (
      <EmptyState title="Aún no hay cajas. Pídele a la administración que cree una." />
    );
  }

  return (
    <>
      {d.sessions.length > 1 || freeRegisters.length > 0 ? (
        <div className="actions">
          {d.sessions.length > 1 ? (
            <div className="field">
              <label htmlFor="pos-session">Turno</label>
              <select id="pos-session" value={session?.id} onChange={(e) => setChosen(e.target.value)}>
                {d.sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.register.name} · {s.openedBy.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {session && freeRegisters.length > 0 ? (
            <Button variant="ghost" onClick={() => setOpening((o) => !o)}>
              Abrir otra caja
            </Button>
          ) : null}
        </div>
      ) : null}
      {!session || opening ? (
        <Card title="Abrir turno">
          <OpenSessionForm churchId={churchId} registers={freeRegisters} onDone={(m, id) => (setChosen(id), refresh(m))} />
          {canManage ? (
            <details className="small">
              <summary>Nueva caja</summary>
              <NewRegisterForm churchId={churchId} onDone={refresh} />
            </details>
          ) : null}
        </Card>
      ) : null}
      {session ? <PosPanel key={session.id} churchId={churchId} session={session} products={d.products} onChange={refresh} /> : null}
    </>
  );
}

function OpenSessionForm({ churchId, registers, onDone }: { churchId: string; registers: { id: string; name: string }[]; onDone: (m: string, id: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const raw = String(form.get('float') ?? '').trim();
    const cents = raw === '' || /^0+(\.0+)?$/.test(raw) ? 0 : parseMoney(raw);
    if (cents === null) {
      setError('Escribe un monto válido para el fondo de caja.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const s = await api.post<PosSession>(`/commerce/churches/${churchId}/sessions`, { registerId: String(form.get('registerId')), openingFloatCents: cents });
      onDone(`Turno abierto en ${s.register.name}.`, s.id);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  if (registers.length === 0) return <p className="muted small">Todas las cajas tienen un turno abierto.</p>;
  return (
    <form className="form" onSubmit={submit} noValidate>
      <div className="field">
        <label htmlFor="registerId">Caja</label>
        <select id="registerId" name="registerId">
          {registers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>
      <Field label="Fondo inicial en efectivo" name="float" inputMode="decimal" placeholder="0.00" autoComplete="off" hint="El cambio con el que empieza la caja." />
      {error ? <Alert>{error}</Alert> : null}
      <Button type="submit" busy={busy}>
        Abrir turno
      </Button>
    </form>
  );
}

function NewRegisterForm({ churchId, onDone }: { churchId: string; onDone: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = String(new FormData(event.currentTarget).get('name') ?? '');
    setBusy(true);
    setError(null);
    try {
      await api.post(`/commerce/churches/${churchId}/registers`, { name });
      onDone(`Caja "${name.trim()}" creada.`);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit} noValidate>
      <Field label="Nombre de la caja" name="name" required minLength={2} maxLength={60} placeholder="Cafetería" />
      {error ? <Alert>{error}</Alert> : null}
      <Button type="submit" variant="secondary" busy={busy}>
        Crear caja
      </Button>
    </form>
  );
}

// ── Pedidos (cocina / entrega) ─────────────────────────────────────────────

function OrdersTab({ churchId, canManage, onNotice }: { churchId: string; canManage: boolean; onNotice: (m: string | null) => void }) {
  const orders = useAsync((signal) => api.get<{ items: Order[] }>(`/commerce/churches/${churchId}/orders`, signal).then((r) => r.items), [churchId]);
  const last = useRef<Order[] | null>(null);
  if (orders.status === 'ready') last.current = orders.data;
  const list = orders.status === 'ready' ? orders.data : last.current;

  if (orders.status === 'error') return <ErrorState error={orders.error} onRetry={orders.reload} />;
  if (!list) return <Loading />;
  // Cocina atiende en orden de llegada: el pedido más antiguo primero.
  const open = list.filter((o) => o.status === 'paid' && o.fulfillment !== 'delivered').sort((x, y) => x.createdAt.localeCompare(y.createdAt) || x.number - y.number);
  const done = list.filter((o) => o.status === 'refunded' || o.fulfillment === 'delivered');

  return (
    <>
      <KitchenLinks churchId={churchId} canManage={canManage} />
      <div className="actions">
        <Button variant="ghost" onClick={orders.reload}>
          Actualizar
        </Button>
      </div>
      {list.length === 0 ? <EmptyState title="No hay pedidos en los turnos abiertos." /> : null}
      {open.length > 0 ? (
        <section className="kitchen" aria-label="Por entregar">
          {open.map((o) => (
            <OrderCard key={o.id} churchId={churchId} order={o} canManage={canManage} onChange={(m) => (onNotice(m), orders.reload())} />
          ))}
        </section>
      ) : list.length > 0 ? (
        <p className="muted">Todo entregado.</p>
      ) : null}
      {done.length > 0 ? (
        <Card title="Entregados y devueltos">
          <ul className="list">
            {done.map((o) => (
              <OrderRow key={o.id} churchId={churchId} order={o} canManage={canManage} onChange={(m) => (onNotice(m), orders.reload())} />
            ))}
          </ul>
        </Card>
      ) : null}
    </>
  );
}

const describe = (o: Order) => o.items.map((i) => `${i.quantity} × ${i.name}`).join(', ');

function OrderCard({ churchId, order: o, canManage, onChange }: { churchId: string; order: Order; canManage: boolean; onChange: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const next = NEXT_STEP[o.fulfillment];

  async function advance() {
    if (!next) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/commerce/churches/${churchId}/orders/${o.id}/fulfillment`, { to: next.to });
      onChange(`Pedido ${o.number}: ${FULFILLMENT[next.to].toLowerCase()}.`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={`Pedido ${o.number}${o.customerName ? ` · ${o.customerName}` : ''}`} action={<span className={`badge${o.fulfillment === 'ready' ? ' badge--active' : ' badge--pending'}`}>{FULFILLMENT[o.fulfillment]}</span>}>
      <ul className="list">
        {o.items.map((i) => (
          <li key={i.productId} className="list__item">
            <strong>
              {i.quantity} × {i.name}
            </strong>
          </li>
        ))}
      </ul>
      <p className="muted small">
        {money(o.totalCents)} · {PAYMENT[o.paymentMethod]} · {new Date(o.createdAt).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' })}
      </p>
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        {next ? (
          <Button busy={busy} onClick={advance}>
            {next.label}
          </Button>
        ) : null}
        {canManage ? <RefundButton churchId={churchId} order={o} onChange={onChange} /> : null}
      </div>
    </Card>
  );
}

function OrderRow({ churchId, order: o, canManage, onChange }: { churchId: string; order: Order; canManage: boolean; onChange: (m: string) => void }) {
  return (
    <li className="list__item list__item--actions">
      <span>
        <strong>Pedido {o.number}</strong>
        <span className="muted small"> {describe(o)}</span>
        {o.refundReason ? <span className="muted small"> · Devuelto: {o.refundReason}</span> : null}
      </span>
      <span className={`money${o.status === 'refunded' ? ' money--negative' : ''}`}>{money(o.totalCents)}</span>
      {o.status === 'refunded' ? <span className="badge badge--warn">Devuelto</span> : canManage ? <RefundButton churchId={churchId} order={o} onChange={onChange} /> : null}
    </li>
  );
}

/** Devolver regresa el dinero y el inventario. Solo mientras el turno está abierto; lo decide el backend. */
function RefundButton({ churchId, order: o, onChange }: { churchId: string; order: Order; onChange: (m: string) => void }) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/commerce/churches/${churchId}/orders/${o.id}/refund`, { reason: String(new FormData(event.currentTarget).get('reason') ?? '') });
      onChange(`Pedido ${o.number} devuelto.`);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  if (!asking)
    return (
      <Button variant="ghost" onClick={() => setAsking(true)}>
        Devolver
      </Button>
    );
  return (
    <form className="form" onSubmit={submit} noValidate>
      <Field label={`Motivo de la devolución del pedido ${o.number}`} name="reason" required minLength={3} maxLength={300} />
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" variant="danger" busy={busy}>
          Devolver {money(o.totalCents)}
        </Button>
        <Button type="button" variant="ghost" onClick={() => setAsking(false)}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

// ── Productos ──────────────────────────────────────────────────────────────

function ProductsTab({ churchId, onNotice }: { churchId: string; onNotice: (m: string | null) => void }) {
  const me = useMe();
  const [version, setVersion] = useState(0);
  const [adding, setAdding] = useState(false);
  const catalog = useAsync((signal) => api.get<Catalog>(`/commerce/churches/${churchId}/catalog`, signal), [churchId, version]);
  const stock = useAsync(
    (signal) =>
      me.can('inventory.read')
        ? api.get<{ items: InventoryItem[]; locations: InventoryLocation[] }>(`/inventory/churches/${churchId}/stock`, signal)
        : Promise.resolve({ items: [], locations: [] }),
    [churchId],
  );
  const last = useRef<Catalog | null>(null);
  if (catalog.status === 'ready') last.current = catalog.data;
  const data = catalog.status === 'ready' ? catalog.data : last.current;
  const inv = stock.status === 'ready' ? stock.data : { items: [], locations: [] };
  const refresh = (m: string) => {
    onNotice(m);
    setAdding(false);
    setVersion((v) => v + 1);
  };

  if (catalog.status === 'error') return <ErrorState error={catalog.error} onRetry={catalog.reload} />;
  if (!data) return <Loading />;

  return (
    <>
      <div className="actions">
        <Button variant={adding ? 'primary' : 'secondary'} onClick={() => setAdding((a) => !a)}>
          Nuevo producto
        </Button>
      </div>
      {adding ? (
        <Card title="Nuevo producto">
          <ProductForm churchId={churchId} items={inv.items} locations={inv.locations} onDone={refresh} />
        </Card>
      ) : null}
      <Card title="Productos">
        {data.products.length === 0 ? <EmptyState title="Aún no hay productos." /> : null}
        <ul className="list">
          {data.products.map((p) => (
            <ProductRow key={p.id} churchId={churchId} product={p} items={inv.items} onChange={refresh} />
          ))}
        </ul>
      </Card>
    </>
  );
}

function ProductRow({ churchId, product: p, items, onChange }: { churchId: string; product: Product; items: InventoryItem[]; onChange: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const item = p.inventory ? items.find((i) => i.id === p.inventory!.itemId) : undefined;

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/commerce/churches/${churchId}/products/${p.id}`, { name: p.name, priceCents: p.priceCents, active: !p.active, inventory: p.inventory });
      onChange(p.active ? `"${p.name}" ya no aparece en la caja.` : `"${p.name}" vuelve a la venta.`);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <li className="list__item pos__line">
      <span>
        <strong>{p.name}</strong>
        {!p.active ? <span className="badge">Pausado</span> : null}
        {p.inventory ? (
          <span className="muted small">
            {' '}
            · descuenta {p.inventory.quantity} {item ? `${item.unit} de ${item.name}` : 'del inventario'}
          </span>
        ) : null}
      </span>
      <span className="money">{money(p.priceCents)}</span>
      <Button variant="ghost" busy={busy} onClick={toggle}>
        {p.active ? 'Pausar' : 'Activar'}
      </Button>
      {error ? <Alert>{error}</Alert> : null}
    </li>
  );
}

function ProductForm({ churchId, items, locations, onDone }: { churchId: string; items: InventoryItem[]; locations: InventoryLocation[]; onDone: (m: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linked, setLinked] = useState(false);
  const consumables = items.filter((i) => i.kind === 'consumable');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const price = parseMoney(String(form.get('price') ?? ''));
    if (price === null) {
      setError('Escribe un precio válido.');
      return;
    }
    const quantity = Number(String(form.get('quantity') ?? '').replace(',', '.'));
    if (linked && !(quantity > 0)) {
      setError('Escribe cuánto descuenta cada venta.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const p = await api.post<Product>(`/commerce/churches/${churchId}/products`, {
        name: String(form.get('name') ?? ''),
        priceCents: price,
        inventory: linked ? { itemId: String(form.get('itemId')), locationId: String(form.get('locationId')), quantity } : null,
      });
      onDone(`"${p.name}" agregado.`);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={submit} noValidate>
      <Field label="Nombre" name="name" required minLength={2} maxLength={80} placeholder="Café americano" />
      <Field label="Precio" name="price" required inputMode="decimal" autoComplete="off" placeholder="25.00" />
      {consumables.length > 0 && locations.length > 0 ? (
        <label className="choice">
          <input type="checkbox" checked={linked} onChange={(e) => setLinked(e.target.checked)} />
          <span>Cada venta descuenta del inventario</span>
        </label>
      ) : null}
      {linked ? (
        <>
          <div className="field">
            <label htmlFor="itemId">Artículo</label>
            <select id="itemId" name="itemId">
              {consumables.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} ({i.unit})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="locationId">Desde</label>
            <select id="locationId" name="locationId">
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
          <Field label="Cantidad por venta" name="quantity" inputMode="decimal" autoComplete="off" placeholder="0.02" />
        </>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      <Button type="submit" busy={busy}>
        Guardar producto
      </Button>
    </form>
  );
}
